import { useCallback, useEffect, useMemo, useState } from "react";
import { useReportApi, unwrapReport, federationErrorFrom } from "../api/reportApi";

// #751 — Publish Results (USEF/USDF federation submission) hook, one instance
// per show. report-api has NO persisted "competition settings" row for a show
// (docs/score/federation-results-publishing.md — compYear/usefCompId/sectionCd
// are request-body fields on every upload/publish call, not stored server
// side) — but #1004-#1006 stamped those exact values onto score-show
// (usefCompId/usefCompYear) and score-class (sectionCode) at reconcile, straight
// from FVD. So #1007 removes the operator setup/mapping step entirely: the
// caller (PublishResults.jsx) reads usefCompId/compYear off the active show
// record and passes them in here; per-class sectionCd/classNumber/name come
// straight off the class row itself (#1103 — no preview round trip needed).
// IsChampionship is always false (matches manager-api usef.js:205 — nothing
// populates it from data there either). #1103 removed the preview-fetch-gated
// party/judge USEF-id override collection entirely, but #1110 restores the
// JUDGE half of it (#3603 later restores the Show Pass (EX) half of the party
// overrides, declared per party from the upload error): a combined two-judge box
// (two FVD-real judges sharing one score-judge row, #1110) or any other
// #746-lookup miss can never resolve a USEF id from name alone, so staff
// need a manual per-position override. Since there's no preview round trip
// anymore, judgeOverridesByClassId is filled in reactively off the upload/
// correct error itself (PublishResults.jsx parses the "position X" the
// UsefSerializationError issue names) rather than a pre-flight scan. The
// per-class submission STATE MACHINE (Draft/Uploaded/Submitted/Corrected)
// and the audit trail ARE persisted server-side (federation-submission-state
// / federation-audit) — those come from the API.

// #3604: the mailing-address keys usef-serializer buildPartyFields reads from
// partyOverrides[rideId][role] for a party with no USEF ID.
export const PARTY_ADDRESS_KEYS = ["address", "city", "state", "zip"];

// #3579 -- what the server's partyOverrides takes: a typed USEF ID wins and is
// sent alone (an ID needs no NoIDReason or address); otherwise the EX/FEI reason
// and whichever address fields staff filled in. Blank fields and empty parties
// are dropped so a retry without any entry still sends {}.
// #3581 / #3151 -- the key a judge is matched on across classes (same shape as
// report-api's remembered-judge nameKey): lowercased, whitespace collapsed.
export function judgeNameKey(name) {
  return String(name || "").toLowerCase().replace(/\s+/g, " ").trim();
}

const PARTY_FIELDS = ["noIdReason", "address", "city", "state", "zip"];
export function cleanPartyOverrides(byRide) {
  const out = {};
  for (const [rideId, roles] of Object.entries(byRide || {})) {
    for (const [role, raw] of Object.entries(roles || {})) {
      const usefId = String(raw?.usefId ?? "").trim();
      let entry;
      if (usefId) {
        entry = { usefId };
      } else {
        entry = {};
        for (const f of PARTY_FIELDS) {
          const v = String(raw?.[f] ?? "").trim();
          if (v) entry[f] = v;
        }
      }
      if (Object.keys(entry).length !== 0) out[rideId] = { ...out[rideId], [role]: entry };
    }
  }
  return out;
}

export function useFederationResults(showId, usefCompId, compYear) {
  const reportApi = useReportApi();

  // ---- Per-class submission state (AC4) -----------------------------------
  const [classStates, setClassStates] = useState([]);
  // #3151 -- judge USEF ids a human already confirmed for this show (report-api
  // remembers one only after a typed override PASSED the identity check, and
  // applies it as the default override on later publishes, re-verified each time).
  const [confirmedJudges, setConfirmedJudges] = useState([]);
  // #3186 -- `statesLoading` is true while a fetch is in flight AND until the
  // states for the CURRENT show have landed once, so the first render with a
  // showId already reads as loading (an effect-flipped flag was false there).
  const [statesInFlight, setStatesInFlight] = useState(false);
  const [statesLoadedFor, setStatesLoadedFor] = useState(null);
  const statesLoading = statesInFlight || (Boolean(showId) && statesLoadedFor !== showId);
  const reloadClassStates = useCallback(async () => {
    if (!showId) {
      setClassStates([]);
      setConfirmedJudges([]);
      return;
    }
    try {
      setStatesInFlight(true);
      const res = unwrapReport(await reportApi.federation.getFederationShowState(showId));
      setClassStates(res?.classes || []);
      setConfirmedJudges(res?.judgeUsefIds || []);
    } catch {
      setClassStates([]);
      setConfirmedJudges([]);
    } finally {
      setStatesInFlight(false);
      setStatesLoadedFor(showId);
    }
  }, [showId, reportApi]);
  useEffect(() => {
    reloadClassStates();
  }, [reloadClassStates]);

  const stateByClassId = useMemo(() => {
    const map = {};
    for (const row of classStates) map[row.classId] = row.status;
    return map;
  }, [classStates]);

  // #1041 AC2/AC3 — getFederationShowState now derives `stale` per class
  // (fingerprint drift OR the manual "Mark for re-publish" override); this
  // mirrors stateByClassId's shape so PublishResults.jsx's classDerivedState
  // reads it the same way.
  const staleByClassId = useMemo(() => {
    const map = {};
    for (const row of classStates) map[row.classId] = !!row.stale;
    return map;
  }, [classStates]);

  // ---- Audit history (AC4) -------------------------------------------------
  const loadShowAudit = useCallback(async () => {
    if (!showId) return [];
    const res = unwrapReport(await reportApi.federation.getFederationShowAudit(showId));
    return res?.events || [];
  }, [showId, reportApi]);
  const loadClassAudit = useCallback(
    async (classId) => {
      const res = unwrapReport(await reportApi.federation.getFederationClassAudit(classId));
      return res?.events || [];
    },
    [reportApi],
  );

  // ---- USEF 10-day deadline (AC4) ------------------------------------------
  const [deadline, setDeadline] = useState(null);
  const loadDeadline = useCallback(
    async (competitionEndDate) => {
      if (!showId || !competitionEndDate) return;
      try {
        const res = unwrapReport(
          await reportApi.federation.getUsefDeadlineStatus(competitionEndDate, showId),
        );
        setDeadline(res);
      } catch {
        setDeadline(null);
      }
    },
    [showId, reportApi],
  );

  // ---- Judge USEFID overrides (#1110) --------------------------------------
  // classId -> position -> { USEFID }. #746's name lookup can't resolve a
  // combined two-judge box (two real judges sharing one score-judge row,
  // e.g. firstName "Koch" lastName "Gurney") or any other name-lookup miss —
  // usef-serializer's buildJudgeEntry treats the stored name as-is and just
  // needs a manually supplied USEFID to unblock the upload (docs §8: judge
  // USEFID has no NoIDReason escape). Kept in the hook (not local component
  // state) so it's stable across re-renders/retries the same way
  // classErrorsByClassId is.
  const [judgeOverridesByClassId, setJudgeOverridesByClassId] = useState({});
  const setJudgeOverride = useCallback((classId, position, patch) => {
    setJudgeOverridesByClassId((prev) => ({
      ...prev,
      [classId]: {
        ...prev[classId],
        [position]: { ...prev[classId]?.[position], ...patch },
      },
    }));
  }, []);

  // #3581 -- one judge, one entry, the whole show. A judge sits many classes
  // (23 at HITS Del Mar), at a booth position that can differ per class, and the
  // USEF ID is the judge's, not the class's. `judgeUsefIdByName` is the show-wide
  // entry (name key -> id); `setJudgeUsefIdForShow` writes it AND fills
  // judgeOverridesByClassId[classId][position] for every class/position in
  // `targets` (the ones the page has seen name that judge). A class that errors
  // on this judge later picks the id up through `fillJudgeOverridesFromNames`.
  // It reuses setJudgeOverride's state; the server still re-verifies each id.
  const [judgeUsefIdByName, setJudgeUsefIdByName] = useState({});
  const setJudgeUsefIdForShow = useCallback((nameKey, usefId, targets) => {
    setJudgeUsefIdByName((prev) => ({ ...prev, [nameKey]: usefId }));
    setJudgeOverridesByClassId((prev) => {
      const next = { ...prev };
      for (const { classId, position } of targets || []) {
        next[classId] = { ...next[classId], [position]: { ...next[classId]?.[position], USEFID: usefId } };
      }
      return next;
    });
  }, []);
  const fillJudgeOverridesFromNames = useCallback(
    (classId, judges) => {
      setJudgeOverridesByClassId((prev) => {
        let next = prev;
        for (const { name, position } of judges || []) {
          const usefId = judgeUsefIdByName[judgeNameKey(name)];
          if (!usefId || prev[classId]?.[position]?.USEFID) continue;
          if (next === prev) next = { ...prev };
          next[classId] = { ...next[classId], [position]: { ...next[classId]?.[position], USEFID: usefId } };
        }
        return next;
      });
    },
    [judgeUsefIdByName],
  );

  // ---- Rider/owner/trainer USEF ID overrides (#3579) ------------------------
  // classId -> rideId -> role -> { usefId, noIdReason, address, city, state,
  // zip }. A party with no USEF ID blocks the upload (400 usef_validation_error,
  // structuredIssues coded party_missing_usef_id); staff type the ID (or an
  // EX/FEI reason plus address) and the next call sends it as partyOverrides.
  const [partyOverridesByClassId, setPartyOverridesByClassId] = useState({});
  const setPartyOverride = useCallback((classId, rideId, role, patch) => {
    setPartyOverridesByClassId((prev) => ({
      ...prev,
      [classId]: {
        ...prev[classId],
        [rideId]: {
          ...prev[classId]?.[rideId],
          [role]: { ...prev[classId]?.[rideId]?.[role], ...patch },
        },
      },
    }));
  }, []);
  // ---- Show Pass (EX) + address helpers (#3603 / #3604) --------------------
  // Built on setPartyOverride; each action touches only its own keys so a Show
  // Pass declaration, a typed USEF ID and an address never clobber each other.
  // Held per class, not persisted on the person row.
  const clearPartyOverrideKeys = useCallback((classId, rideId, role, keys) => {
    setPartyOverridesByClassId((prev) => {
      const roleEntry = { ...prev[classId]?.[rideId]?.[role] };
      for (const k of keys) delete roleEntry[k];
      const rideEntry = { ...prev[classId]?.[rideId] };
      if (Object.keys(roleEntry).length === 0) delete rideEntry[role];
      else rideEntry[role] = roleEntry;
      const classEntry = { ...prev[classId] };
      if (Object.keys(rideEntry).length === 0) delete classEntry[rideId];
      else classEntry[rideId] = rideEntry;
      return { ...prev, [classId]: classEntry };
    });
  }, []);
  const declareShowPass = useCallback(
    (classId, rideId, role) => setPartyOverride(classId, rideId, role, { noIdReason: "EX" }),
    [setPartyOverride],
  );
  const undoShowPass = useCallback(
    (classId, rideId, role) => clearPartyOverrideKeys(classId, rideId, role, ["noIdReason"]),
    [clearPartyOverrideKeys],
  );
  const setPartyAddress = useCallback(
    (classId, rideId, role, addr) =>
      setPartyOverride(classId, rideId, role, {
        address: addr.address,
        city: addr.city,
        state: addr.state,
        zip: addr.zip,
      }),
    [setPartyOverride],
  );
  const undoPartyAddress = useCallback(
    (classId, rideId, role) => clearPartyOverrideKeys(classId, rideId, role, PARTY_ADDRESS_KEYS),
    [clearPartyOverrideKeys],
  );

  // ---- Class-level publish payload builder ---------------------------------
  // #1007 — compYear/usefCompId come straight from the show record (no more
  // operator setup step).
  // #1103 — sourced directly from the score-class ROW (classesById[classId]
  // in PublishResults.jsx), not from a preview-derived classResult: the
  // preview gate is gone, so publish must work with only classId + the class
  // row in hand. This is also the fix for the classNumber bug (AC6) — the
  // old code read classResult?.class?.classNumber, but report-api's preview
  // assemble (dressage-result-model.js's assembleClassResult) never put
  // classNumber on that `class` sub-object in the first place, so it was
  // always null. classNumber/sectionCode/name all live on the class row
  // itself. #1103 dropped the party/judge USEF-id override collection that
  // lived on top of the preview fetch entirely — per the card, this FE
  // doesn't pre-collect missing federation data, it surfaces what the
  // upload call reports missing. #1110 restores the JUDGE half: party
  // overrides stay empty (that gap is fixed in FVD + republished, per
  // #1103's intent), but judgeOverrides now carries whatever the operator
  // has filled in via setJudgeOverride for this class.
  const buildClassBody = useCallback(
    (classId, classRow) => {
      const sectionCode = classRow?.sectionCode;
      const hasSectionCode = sectionCode !== undefined && sectionCode !== null && sectionCode !== "";
      return {
        compYear: Number(compYear),
        usefCompId: Number(usefCompId),
        classNumber: classRow?.classNumber ?? null,
        sectionCd: hasSectionCode ? Number(sectionCode) : undefined,
        classTitle: classRow?.className,
        isChampionship: false,
        partyOverrides: cleanPartyOverrides(partyOverridesByClassId[classId]),
        disciplineOverrides: {},
        judgeOverrides: judgeOverridesByClassId[classId] || {},
      };
    },
    [compYear, usefCompId, judgeOverridesByClassId, partyOverridesByClassId],
  );

  // ---- Actions (AC3, AC4) — each records into per-class state + audit -----
  const [actionBusy, setActionBusy] = useState(null); // classId | "show-usef" | "show-usdf" | null
  const [actionError, setActionError] = useState(null);

  const withAction = useCallback(
    (key, fn) =>
      async (...args) => {
        setActionBusy(key);
        setActionError(null);
        try {
          const result = await fn(...args);
          await reloadClassStates();
          return result;
        } catch (err) {
          const normalized = federationErrorFrom(err);
          setActionError({ key, ...normalized });
          throw normalized;
        } finally {
          setActionBusy(null);
        }
      },
    [reloadClassStates],
  );

  const uploadClass = useCallback(
    (classId, classRow) =>
      withAction(classId, async () => {
        const body = buildClassBody(classId, classRow);
        return unwrapReport(await reportApi.federation.uploadUsefClass(classId, body));
      })(),
    [withAction, buildClassBody, reportApi],
  );

  const correctClass = useCallback(
    (classId, classRow) =>
      withAction(classId, async () => {
        const body = buildClassBody(classId, classRow);
        return unwrapReport(await reportApi.federation.correctUsefClass(classId, body));
      })(),
    [withAction, buildClassBody, reportApi],
  );

  const deleteClass = useCallback(
    (classId, classNumber) =>
      withAction(classId, async () => {
        const body = {
          showId,
          compYear: Number(compYear),
          usefCompId: Number(usefCompId),
          classNumber,
        };
        return unwrapReport(await reportApi.federation.deleteUsefClass(classId, body));
      })(),
    [withAction, showId, compYear, usefCompId, reportApi],
  );

  // #1041 AC3 — operator override: force the stale flag regardless of the
  // fingerprint compare. No class-result/payload needed (never calls USEF),
  // just flips the flag server-side; reloadClassStates (via withAction) picks
  // up the new `stale: true` so the badge updates immediately.
  const markForRepublish = useCallback(
    (classId) =>
      withAction(classId, async () => {
        const body = { showId };
        return unwrapReport(await reportApi.federation.markClassForRepublish(classId, body));
      })(),
    [withAction, showId, reportApi],
  );

  // #1347 — the DELIBERATE resolve of a stuck USEF_DELETE_UNCOMMITTED marker
  // (deleteclass already succeeded at USEF, only the local commit is
  // outstanding). Never a side effect of the skip switch. No USEF call.
  const resolveDeleteUncommitted = useCallback(
    (classId) =>
      withAction(classId, async () => {
        const body = { showId };
        return unwrapReport(await reportApi.federation.resolveUsefDeleteUncommitted(classId, body));
      })(),
    [withAction, showId, reportApi],
  );

  // #3151 -- forget a remembered judge USEF id (a wrong one, or a judge who
  // changed). No USEF call; typing a different id that passes replaces it too.
  const clearConfirmedJudge = useCallback(
    (nameKey) =>
      withAction(`judge-${nameKey}`, async () => {
        return unwrapReport(await reportApi.federation.clearJudgeUsefId(showId, { nameKey }));
      })(),
    [withAction, showId, reportApi],
  );

  const finalizeClass = useCallback(
    (classId, classNumber) =>
      withAction(classId, async () => {
        const body = {
          showId,
          compYear: Number(compYear),
          usefCompId: Number(usefCompId),
          classNumber,
        };
        return unwrapReport(await reportApi.federation.finalizeUsefClass(classId, body));
      })(),
    [withAction, showId, compYear, usefCompId, reportApi],
  );

  // #1038 — Finalize: the one-way submitcompetition lock (#1047's submit-only
  // endpoint). The request body only needs each class's classId — the
  // classes are already UPLOADED by the time Finalize runs, submitcompetition
  // just closes them out. Response shape is {classId, transitioned,
  // classification} classStates[], so the caller's recordClassClassification
  // loop is unchanged. #1103 — takes plain class rows (classesById values,
  // keyed by `.id`), not preview classResults; publishUsef/publishUsefShow
  // (the combined upload+submit walk) was already dead on this page (#1038
  // moved Publish to the per-class upload walk below) and is removed here
  // along with the last of the preview-classResult shape.
  const submitUsef = useCallback(
    (classRows) =>
      withAction("submit-usef", async () => {
        const classes = classRows.map((c) => ({ classId: c.id }));
        const body = { compYear: Number(compYear), usefCompId: Number(usefCompId), classes };
        return unwrapReport(await reportApi.federation.submitUsefShow(showId, body));
      })(),
    [withAction, compYear, usefCompId, showId, reportApi],
  );

  // #1086 — deliver defaults to "email" (unchanged behavior, #1056 recipient);
  // "download" runs the exact same server-side serializer call but skips the
  // email/send-record/audit, returning { filename, datContent } instead so
  // the caller (PublishResults.jsx) can trigger a browser download.
  const publishUsdf = useCallback(
    (deliver = "email") =>
      withAction("show-usdf", async () => {
        const body = { compYear: Number(compYear), usefCompId: Number(usefCompId), deliver };
        return unwrapReport(await reportApi.federation.publishUsdfShow(showId, body));
      })(),
    [withAction, compYear, usefCompId, showId, reportApi],
  );

  return {
    classStates,
    stateByClassId,
    staleByClassId,
    statesLoading,
    reloadClassStates,
    loadShowAudit,
    loadClassAudit,
    deadline,
    loadDeadline,
    actionBusy,
    actionError,
    setActionError,
    judgeOverridesByClassId,
    setJudgeOverride,
    judgeUsefIdByName,
    setJudgeUsefIdForShow,
    fillJudgeOverridesFromNames,
    partyOverridesByClassId,
    setPartyOverride,
    declareShowPass,
    undoShowPass,
    setPartyAddress,
    undoPartyAddress,
    confirmedJudges,
    clearConfirmedJudge,
    uploadClass,
    correctClass,
    deleteClass,
    markForRepublish,
    resolveDeleteUncommitted,
    finalizeClass,
    submitUsef,
    publishUsdf,
  };
}

export default useFederationResults;
